// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { sharedStyles } from '../../styles/shared-styles.js';
import { event, ShuiLitElement } from '../../utils/event-decorator.js';
import timepickerStyles from './sh-timepicker.lit.scss.js';
import { TimeModifierMixin } from './time-modifier-mixin.js';
/**
 * @fires time-value-changed {TimeValueChangedEvent} - *hide internal event fired when value(h/m/s) changes which is listened to in timepicker to updates it's value
 */
let SHTimepickerPanel = class SHTimepickerPanel extends TimeModifierMixin(ShuiLitElement) {
  constructor() {
    super(...arguments);
    /**Defines hours field of the Timepicker */
    this.h = '';
    /**Defines minutes field of the Timepicker */
    this.m = '';
    /**Defines seconds field of the Timepicker */
    this.s = '';
    /**Defines am/pm field of the Timepicker, applicable when `clock-format` is set to `12-hour` */
    this.amPm = '';
    /**Defines alternate format HH:MM AM/PM for Timepicker */
    this.mini = false;
    /**Defines increment/decrement step for minute and second selector (5,10,15,30). Default value is `1` */
    this.step = 1;
    /**Defines whether time mode is 12-hour / 24-hour . Possible values are `"12-hour"` or `"24-hour"` */
    this.clockFormat = '12-hour';
    /**If set the panel is open for the timepicker.*/
    this.active = false;
  }
  static get styles() {
    return [timepickerStyles, sharedStyles];
  }
  render() {
    return html`
      <div class="timepicker-panel-wrapper">
        <div class="timepicker-panel">
          <div class="timepicker-controls-wrapper">
            <div class="timepicker-controls hour-control">
              <sh-icon
                icon="up-s"
                id="dropdown-icon"
                data-id="h"
                button=""
                size="m"
                @click=${this.incrementWithButton}
              ></sh-icon>
              <div class="control-value">
                <input
                  type="text"
                  readonly
                  placeholder="hh"
                  data-id="h-ip"
                  id="h"
                  min="1"
                  max="${this.clockFormat === '12-hour' ? 12 : 23}"
                  .value="${this.h}"
                  @keydown=${this.onArrowKeyUpOrDownPressUpdateTime}
                  size="2"
                />
              </div>
              <sh-icon
                icon="down-s"
                id="dropdown-icon"
                data-id="h"
                button=""
                size="m"
                @click=${this.decrementWithButton}
              ></sh-icon>
            </div>
            <div
              class="seperator ${!(
                this.h === '' &&
                this.m === '' &&
                this.s === '' &&
                this.amPm === ''
              )
                ? 'colon-active'
                : ''}"
            >
              :
            </div>
            <div class="timepicker-controls min-control">
              <sh-icon
                icon="up-s"
                id="dropdown-icon"
                data-id="m"
                button=""
                size="m"
                @click=${this.incrementWithButton}
              ></sh-icon>
              <div class="control-value">
                <input
                  type="text"
                  readonly
                  placeholder="mm"
                  data-id="m-ip"
                  id="m"
                  min="0"
                  max="59"
                  size="2"
                  maxlength="2"
                  .value="${this.m}"
                  @keydown=${this.onArrowKeyUpOrDownPressUpdateTime}
                  size="2"
                />
              </div>
              <sh-icon
                icon="down-s"
                id="dropdown-icon"
                data-id="m"
                button=""
                size="m"
                @click=${this.decrementWithButton}
              ></sh-icon>
            </div>
            ${!this.mini
              ? html` <div
                    class="seperator ${!(
                      this.h === '' &&
                      this.m === '' &&
                      this.s === '' &&
                      this.amPm === ''
                    )
                      ? 'colon-active'
                      : ''} full"
                  >
                    :
                  </div>
                  <div class="timepicker-controls sec-control full">
                    <sh-icon
                      icon="up-s"
                      id="dropdown-icon"
                      data-id="s"
                      button=""
                      size="m"
                      @click=${this.incrementWithButton}
                    ></sh-icon>
                    <div class="control-value">
                      <input
                        type="text"
                        readonly
                        placeholder="ss"
                        data-id="s-ip"
                        class="full"
                        id="s"
                        min="0"
                        max="59"
                        maxlength="2"
                        .value="${this.s}"
                        @keydown=${this.onArrowKeyUpOrDownPressUpdateTime}
                        size="2"
                      />
                    </div>
                    <sh-icon
                      icon="down-s"
                      id="dropdown-icon"
                      data-id="s"
                      button=""
                      size="m"
                      @click=${this.decrementWithButton}
                    ></sh-icon>
                  </div>`
              : ''}
            <div
              class="seperator ${!(
                this.h === '' &&
                this.m === '' &&
                this.s === '' &&
                this.amPm === ''
              )
                ? 'colon-active'
                : ''}"
            ></div>
            ${this.clockFormat === '12-hour'
              ? html` <div class="timepicker-controls ampm-control">
                  <sh-icon
                    icon="up-s"
                    id="dropdown-icon"
                    button=""
                    size="m"
                    data-id="ap"
                    @click=${this.incrementWithButton}
                  ></sh-icon>
                  <div class="control-value">
                    <input
                      type="text"
                      readonly
                      placeholder="AM"
                      ?readonly="true"
                      data-id="ap-ip"
                      id="amOrPm"
                      .value="${this.amPm}"
                      size="2"
                      maxlength="2"
                      @keydown=${this.onArrowKeyUpOrDownPressUpdateTime}
                    />
                  </div>
                  <sh-icon
                    icon="down-s"
                    id="dropdown-icon"
                    button=""
                    size="m"
                    data-id="ap"
                    @click=${this.decrementWithButton}
                  ></sh-icon>
                </div>`
              : ''}
          </div>
        </div>
      </div>
    `;
  }
  onArrowKeyUpOrDownPressUpdateTime(e) {
    this.incrementOrDecrementOnKeyDown(e);
  }
  incrementWithButton(e) {
    e.stopPropagation();
    this.incrementTime(e.target);
  }
  decrementWithButton(e) {
    e.stopPropagation();
    this.decrementTime(e.target);
  }
};
__decorate([event()], SHTimepickerPanel.prototype, 'timeValueChangedEvent', void 0);
__decorate([property({ type: String, reflect: true })], SHTimepickerPanel.prototype, 'h', void 0);
__decorate([property({ type: String, reflect: true })], SHTimepickerPanel.prototype, 'm', void 0);
__decorate([property({ type: String, reflect: true })], SHTimepickerPanel.prototype, 's', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'am-pm' })],
  SHTimepickerPanel.prototype,
  'amPm',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHTimepickerPanel.prototype,
  'mini',
  void 0
);
__decorate(
  [property({ type: Number, reflect: true })],
  SHTimepickerPanel.prototype,
  'step',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'clock-format' })],
  SHTimepickerPanel.prototype,
  'clockFormat',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHTimepickerPanel.prototype,
  'active',
  void 0
);
SHTimepickerPanel = __decorate([customElement('sh-timepicker-panel')], SHTimepickerPanel);
export { SHTimepickerPanel };
